import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Row,
  Col,
  Button,
  Typography,
  Switch,
  Checkbox,
  Divider,
  Badge,
  Pagination,
  message,
  Spin,
} from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { NotificationPill } from './index';
import '../../../styles/Global.less';
import API from '../../../api/API';
import { UserContext } from '../../../contexts';
import { INotificationPreference } from '../../../models/notificationPreference';
import {
  INotificationDeliveryChannel,
  INotificationDeliveryFormat,
  NotificationDeliveryId,
} from '../../../models/notificationDeliveryFormat';
import { LoadingScreen } from '../event';
import { INotification } from '../../../models/notification';
import subscribeUser from '../../../subscription';

const { Title } = Typography;
const LIMIT = 10;

const Notifications: React.FC = () => {
  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [fetchingComments, setFetchingComments] = useState(true);
  const [notificationPreference, setNotificationPreference] = useState<
    INotificationPreference
  >({ username: user.username, enabled: false, frequency: 0 });
  const [
    notificationDeliveryFormats,
    setNotificationDeliveryFormats,
  ] = useState<INotificationDeliveryFormat>({
    text: {
      notificationDeliveryId: NotificationDeliveryId.TEXT,
      username: user.username,
      enabled: false,
    },
    email: {
      notificationDeliveryId: NotificationDeliveryId.EMAIL,
      username: user.username,
      enabled: false,
    },
  });
  const [notificationCount, setNotificationCount] = useState(0);
  const [notifications, setNotifications] = useState<INotification[]>([]);
  const [selectedNotifications, setSelectedNotifications] = useState<number[]>(
    [],
  );
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    document.title = 'EHE Tracker - Notifications';
  }, []);

  useEffect(() => {
    const changeNotificationDeliveryFormatState = (
      newFormats: INotificationDeliveryChannel[],
    ) => {
      let currentFormats = { ...notificationDeliveryFormats };

      newFormats.forEach((format: INotificationDeliveryChannel) => {
        if (format.notificationDeliveryId === NotificationDeliveryId.TEXT)
          currentFormats = { ...currentFormats, text: format };
        else if (format.notificationDeliveryId === NotificationDeliveryId.EMAIL)
          currentFormats = { ...currentFormats, email: format };
      });

      setNotificationDeliveryFormats(currentFormats);
    };

    const fetchData = async () => {
      const reqNotificationPreference = API.get(
        `/notification/${user.username}/preference`,
      );
      const reqNotificationDeliveryFormats = API.get(
        `/notification/${user.username}/channel`,
      );
      const reqNotificationCount = API.get(`/notification/${user.username}`, {
        params: {
          limit: LIMIT,
          offset: 0,
          read: false,
        },
      });

      const [
        respNotificationPreference,
        respNotificationDeliveryFormats,
        respNotificationCount,
      ] = await Promise.all([
        reqNotificationPreference,
        reqNotificationDeliveryFormats,
        reqNotificationCount,
      ]);

      if (respNotificationPreference.data.length)
        setNotificationPreference(respNotificationPreference.data[0]);
      if (respNotificationDeliveryFormats.data.length) {
        changeNotificationDeliveryFormatState(
          respNotificationDeliveryFormats.data,
        );
      }
      if (respNotificationCount.data.length) {
        setNotificationCount(respNotificationCount.data[0].total);
      }
      setLoading(false);
    };

    try {
      fetchData();
    } catch {
      setLoading(true);
      message.error('Looks like something went wrong.');
    }
    // eslint-disable-next-line
  }, []);

  const fetchNotifications = useCallback(
    async (newOffset: number = offset) => {
      setFetchingComments(true);
      const resp = await API.get(`/notification/${user.username}`, {
        params: {
          limit: LIMIT,
          offset: newOffset,
        },
      });
      setNotifications(resp.data);
      setFetchingComments(false);
    },
    [user.username, offset],
  );

  useEffect(() => {
    if (notificationPreference.enabled) {
      try {
        subscribeUser();
        fetchNotifications();
      } catch {
        setFetchingComments(true);
        message.error('Looks like something went wrong.');
      }
    }
  }, [fetchNotifications, notificationPreference.enabled]);

  const onNotificationSwitchChange = (e: boolean) => {
    if (e) subscribeUser();

    const newNotificationPreference = { ...notificationPreference, enabled: e };
    setNotificationPreference(newNotificationPreference);
    API.post(
      `/notification/${user.username}/preference`,
      newNotificationPreference,
    );
  };

  const onDeliveryFormatCheckboxChange = (e: CheckboxChangeEvent) => {
    let updatedFormat: INotificationDeliveryChannel;

    if (e.target.name === 'textCheckbox') {
      updatedFormat = {
        ...notificationDeliveryFormats.text,
        enabled: e.target.checked,
      };
      setNotificationDeliveryFormats({
        ...notificationDeliveryFormats,
        text: updatedFormat,
      });
    } else {
      updatedFormat = {
        ...notificationDeliveryFormats.email,
        enabled: e.target.checked,
      };
      setNotificationDeliveryFormats({
        ...notificationDeliveryFormats,
        email: updatedFormat,
      });
    }

    API.post(`/notification/${user.username}/channel`, updatedFormat);
  };

  const onNotificationCheckboxChange = (
    e: CheckboxChangeEvent,
    notification: INotification,
  ) => {
    if (!selectedNotifications.some((n) => n === notification.id)) {
      setSelectedNotifications([...selectedNotifications, notification.id]);
    } else {
      setSelectedNotifications(
        selectedNotifications.filter((cur) => cur !== notification.id),
      );
    }
  };

  const onButtonClick = async (type: string) => {
    if (selectedNotifications.length) {
      if (type === 'READ') {
        await API.put(`/notification/${user.username}`, null, {
          params: { ids: selectedNotifications },
        });
      } else {
        await API.delete(`/notification/${user.username}`, {
          params: { ids: selectedNotifications },
        });
      }
      setSelectedNotifications([]);
      const newNotificationCount =
        notificationCount - selectedNotifications.length;
      setNotificationCount(newNotificationCount > 0 ? newNotificationCount : 0);
      await fetchNotifications(offset);
    }
  };

  const onPaginationChange = (page: number) => {
    const newOffset = (page - 1) * LIMIT;
    setSelectedNotifications([]);
    setOffset(newOffset);
    fetchNotifications(newOffset);
  };

  const generateNotificationsText = () => {
    let displayText = 'You have no new notifications.';

    if (notificationCount === 1) {
      displayText = 'You have 1 new notification';
    }
    if (notificationCount > 1) {
      displayText = `You have ${notificationCount} new notifications.`;
    }

    return displayText;
  };

  return loading ? (
    <LoadingScreen />
  ) : (
    <Row>
      <Col span={24}>
        <Title>Notifications</Title>
        <Row gutter={36}>
          <Col>
            <Row gutter={16}>
              <Col>
                <Switch
                  defaultChecked={notificationPreference.enabled}
                  onChange={onNotificationSwitchChange}
                />
              </Col>
              <Col>
                <Typography>
                  {notificationPreference.enabled
                    ? 'Notifications are on.'
                    : 'Notifications have been turned off.'}
                </Typography>
              </Col>
            </Row>
          </Col>
          {notificationPreference.enabled && (
            <Col>
              <Title level={4}>Delivery Format</Title>
              <Row>
                <Checkbox
                  name="textCheckbox"
                  defaultChecked={notificationDeliveryFormats.text.enabled}
                  onChange={onDeliveryFormatCheckboxChange}
                >
                  Text Notifications
                </Checkbox>
              </Row>
              <Row className="mt-0_5">
                <Checkbox
                  name="emailCheckbox"
                  defaultChecked={notificationDeliveryFormats.email.enabled}
                  onChange={onDeliveryFormatCheckboxChange}
                >
                  Email Notifications
                </Checkbox>
              </Row>
            </Col>
          )}
        </Row>
        {notificationPreference.enabled && (
          <>
            <Divider />
            {fetchingComments ? (
              <Row justify="center">
                <Spin />
              </Row>
            ) : (
              <>
                <Title level={3}>{generateNotificationsText()}</Title>
                <Row gutter={36}>
                  <Col>
                    <Row>
                      <Button
                        type="primary"
                        size="large"
                        onClick={() => onButtonClick('READ')}
                        disabled={selectedNotifications.length === 0}
                      >
                        Mark as Read
                      </Button>
                    </Row>
                    <Row className="mt-1">
                      <Button
                        size="large"
                        className="w-100"
                        onClick={() => onButtonClick('DELETE')}
                        disabled={selectedNotifications.length === 0}
                      >
                        Delete
                      </Button>
                    </Row>
                  </Col>
                  <Col>
                    <Row>
                      <Badge status="success" text="New/Unread" />
                    </Row>
                  </Col>
                </Row>
                {notifications.map((n) => (
                  <NotificationPill
                    key={`comment:${n.id}`}
                    notification={n}
                    onChange={onNotificationCheckboxChange}
                  />
                ))}
                <Row className="mt-1">
                  <Pagination
                    defaultCurrent={offset / LIMIT + 1}
                    total={notifications.length && notifications[0].total}
                    onChange={onPaginationChange}
                  />
                </Row>
              </>
            )}
          </>
        )}
      </Col>
    </Row>
  );
};

export default Notifications;
